import { html, type PropertyValues } from 'lit';
import { ContextProvider } from '@lit-labs/context';

import fieldContext from './field.context';
import { Component } from '../component';
import { attachContextRoot } from '../../utils/context-root';
import type { FieldStatus, FieldSize } from './field.types';

import '../layout/group';
import '../layout/stack';
import '../close-button';

import './field-hint';
import './field-label';

export interface FieldProps {
	hint?: string;
	label?: string;
	size?: FieldSize;
	status?: FieldStatus;
	required?: boolean;
}

attachContextRoot();

export class Field extends Component {
	static override get properties() {
		return {
			label: { type: String },
			hint: { type: String },
			required: { type: Boolean },
			status: { type: String },
			size: { type: String },
		};
	}

	/**
	 * Label that describes what this field is about.
	 */
	declare label: FieldProps['label'];

	/**
	 * Auxiliary message to help the user with the field purpose or its `status`.
	 */
	declare hint: FieldProps['hint'];

	/**
	 * Is this field required.
	 */
	declare required: FieldProps['required'];

	/**
	 * Field [validation] status.
	 */
	declare status: FieldProps['status'];

	/**
	 * Field size.
	 */
	declare size: FieldProps['size'];

	private provider = new ContextProvider(this, fieldContext, {
		required: false,
		size: 'default',
		status: 'default',
	});

	constructor() {
		super();

		this.required = false;
		this.status = 'default';
		this.size = 'default';
	}

	override update(changedProperties: PropertyValues) {
		super.update(changedProperties);

		if (
			changedProperties.has('required') ||
			changedProperties.has('status') ||
			changedProperties.has('size')
		) {
			this.provider.setValue({
				required: this.required,
				status: this.status,
				size: this.size,
			});
		}
	}

	override render() {
		return html`<m-stack gap="spacing-1">
			${this.label
				? html`<m-field-label>${this.label}</m-field-label>`
				: html`<slot name="label"></slot>`}

			<slot></slot>

			${this.hint
				? html`<m-field-hint>${this.hint}</m-field-hint>`
				: html`<slot name="hint"></slot>`}
		</m-stack>`;
	}
}

window.customElements.define('m-field', Field);

declare global {
	interface HTMLElementTagNameMap {
		'm-field': Field;
	}
}
